import React, { useEffect, useMemo, useState } from 'react';
import DataTable from 'components/DataTable';
import qs from 'query-string';
import { DATE_FORMATS, formatDate, getPageLimit, showError, updatePageLimit } from 'utils';
import { tableStyles } from '../../../css/tableStyles';
import styles from './index.module.scss';
import Filter from './filters';
import CustomLink from 'components/TableLink';
import { fetchAllUserCarts } from 'services/api';
import Currency from 'components/Currency/Currency';

let filters: { [p: string]: any; page: number } = {
  page: 0,
};

const UsersCartsList = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const [carts, setCarts] = useState<Record<string, any>>([]);

  const fetchAllUserCartsFn = async () => {
    const { page, ...rest } = filters;
    setLoading(true);
    try {
      const response = await fetchAllUserCarts({
        page,
        limit: getPageLimit(),
        filter: {
          ...rest,
        },
      });
      setCarts(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllUserCartsFn();
  }, []);

  const columns = useMemo(() => [
    {
      name: 'Id',
      cell: row => (
        <CustomLink
          text={row?.objectId}
          linkTo={`/user-cart-details/${row?.objectId}`} 
        />
      ),
    },
    {
      name: 'Status',
      selector: 'status',
      sortable: true,
      // eslint-disable-next-line react/display-name
      format: (row) => (
        <div className={styles['name']}>
          {row.status}
        </div>
      ),
    },
    {
      name: 'No. of items',
      selector: 'items',
      sortable: true,
      format: (row) => (
        <div className={styles['name']}>
          {Object.keys(row.items || {})?.length || 0}
        </div>
      ),
    },
    {
      name: 'UserId',
      selector: 'user',
      // eslint-disable-next-line react/display-name
      cell: row => (
        <CustomLink
          text={row?.user?.id}
          linkTo={`/users/${row?.user?.id}`}
        />
      ),
    },
    {
      name: 'Created At',
      sortable: true,
      selector: 'createdAt',
      // eslint-disable-next-line react/display-name
      format: row => formatDate(row.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Last Updated At',
      sortable: true,
      selector: 'updatedAt',
      // eslint-disable-next-line react/display-name
      format: row => formatDate(row.updatedAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A),
    },
    {
      name: 'Total',
      selector: 'total',
      sortable: true,
      format: (row) => <Currency amount={row.total} />,
    },
  ], []);


  const onFilterSubmit = (updatedFilter: Record<string, any>) => {
    filters = { ...updatedFilter, page: 0 };
    updateQueryString();
    fetchAllUserCartsFn();
  };

  const filterToQueryString = () => {
    return qs.stringify({ ...filters });
  };

  const updateQueryString = () => {
    const qs = filterToQueryString();
    props.history.push({
      pathname: props.location.pathname,
      search: qs,
    });
  };

  const onChangePage = (page: number) => {
    filters.page = page - 1;
    updateQueryString();
    fetchAllUserCartsFn();
  };

  const onChangeRowsPerPage = (rowsPerPage: number, currentPage: number) => {
    filters.limit = rowsPerPage;
    updatePageLimit(rowsPerPage);
    onChangePage(currentPage);
  };

  return (
    <div>
      <div className={styles['header']}>
        <div className={styles['heading']}>Users Carts List</div>
      </div>
      <Filter {...props} onSubmit={onFilterSubmit} />
      <DataTable
        keyField="objectId"
        columns={columns}
        noHeader={true}
        data={carts?.items}
        highlightOnHover={true}
        customStyles={tableStyles}
        pagination={true}
        paginationServer={true}
        paginationPerPage={getPageLimit()}
        paginationDefaultPage={(filters.page || 0) + 1}
        paginationTotalRows={carts.total}
        paginationRowsPerPageOptions={[50, 100, 200]}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        loading={loading}
      />
    </div>
  );
};

export  { UsersCartsList };
